import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FaUser,
    FaUserCircle,
    FaIdCard,
    FaMobileAlt,
    FaPhone,
    FaSave,
    FaTimes,
    FaEdit
} from 'react-icons/fa';
import { request } from '../../services/api';
import styles from './UserMenu.module.css';

const getStoredUser = () => {
    try {
        const storedUser =
            localStorage.getItem('user') ||
            localStorage.getItem('currentUser');

        if (!storedUser) return null;

        return JSON.parse(storedUser);
    } catch (error) {
        console.error('خطا در خواندن اطلاعات کاربر:', error);
        return null;
    }
};

const normalizeUserInfo = (source = {}) => {
    return {
        firstName:
            source.firstName ??
            source.first_name ??
            source.name ??
            '',

        lastName:
            source.lastName ??
            source.last_name ??
            source.family ??
            source.family_name ??
            '',

        username:
            source.username ??
            source.userName ??
            source.user_name ??
            '',

        mobile:
            source.mobile ??
            source.phone ??
            source.mobile1 ??
            source.main_mobile ??
            source.phone_num1 ??
            '',

        mobile2:
            source.mobile2 ??
            source.phone2 ??
            source.second_mobile ??
            source.secondPhone ??
            source.phone_num2 ??
            ''
    };
};

const UserInfoModal = ({
    isOpen,
    onClose,
    user = null,
    onSaveUserInfo
}) => {
    const [userInfo, setUserInfo] = useState(
        normalizeUserInfo(user || getStoredUser() || {})
    );

    const [editedUserInfo, setEditedUserInfo] = useState(userInfo);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
    if (!isOpen) return;

    const fallbackUserInfo = normalizeUserInfo(user || getStoredUser() || {});

    setUserInfo(fallbackUserInfo);
    setEditedUserInfo(fallbackUserInfo);
    setIsEditing(false);
    setMessage('');

    loadUserInfoFromApi();
}, [isOpen, user]);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setEditedUserInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleStartEdit = () => {
        setEditedUserInfo(userInfo);
        setIsEditing(true);
        setMessage('');
    };

    const handleCancel = () => {
        setEditedUserInfo(userInfo);
        setIsEditing(false);
        setMessage('');
    };

    const loadUserInfoFromApi = async () => {
    try {
        setMessage('');

        const response = await request('/user_get_profile.php', 'GET');

        if (response?.status !== 'success') {
            throw new Error(response?.message || 'خطا در دریافت اطلاعات کاربر');
        }

        const nextUserInfo = normalizeUserInfo(response.user || {});

        setUserInfo(nextUserInfo);
        setEditedUserInfo(nextUserInfo);

        localStorage.setItem(
            'user',
            JSON.stringify({
                ...(response.user || {}),
                firstName: nextUserInfo.firstName,
                lastName: nextUserInfo.lastName,
                mobile: nextUserInfo.mobile,
                mobile2: nextUserInfo.mobile2
            })
        );

    } catch (error) {
        console.error('خطا در دریافت اطلاعات کاربر:', error);
        setMessage('خطا در دریافت اطلاعات کاربر');
    }
};

    const handleSave = async () => {
    const payload = {
        name: editedUserInfo.firstName.trim(),
        family: editedUserInfo.lastName.trim(),
        phone_num1: editedUserInfo.mobile.trim(),
        phone_num2: editedUserInfo.mobile2.trim()
    };

    try {
        setIsSaving(true);
        setMessage('');

        const response = await request(
            '/user_update_profile.php',
            'POST',
            payload
        );

        if (response?.status !== 'success') {
            throw new Error(response?.message || 'خطا در ذخیره اطلاعات کاربر');
        }

        const nextUserInfo = normalizeUserInfo(response.user || {});

        setUserInfo(nextUserInfo);
        setEditedUserInfo(nextUserInfo);

        localStorage.setItem(
            'user',
            JSON.stringify({
                ...(response.user || {}),
                firstName: nextUserInfo.firstName,
                lastName: nextUserInfo.lastName,
                mobile: nextUserInfo.mobile,
                mobile2: nextUserInfo.mobile2
            })
        );

        setIsEditing(false);
        setMessage(response.message || 'اطلاعات با موفقیت ذخیره شد.');

    } catch (error) {
        console.error('خطا در ذخیره اطلاعات کاربر:', error);
        setMessage(error.message || 'خطا در ذخیره اطلاعات کاربر');
    } finally {
        setIsSaving(false);
    }
};

    const renderInfoRow = (icon, label, value) => {
        return (
            <div className={styles.userInfoRow}>
                <div className={styles.userInfoLabel}>
                    {icon}
                    <span>{label}</span>
                </div>

                <div className={styles.userInfoValue}>
                    {value || '---'}
                </div>
            </div>
        );
    };

    const renderInputRow = (icon, label, field, placeholder) => {
        return (
            <label className={styles.userInfoInputRow}>
                <span className={styles.userInfoInputLabel}>
                    {icon}
                    <span>{label}</span>
                </span>

                <input
                    type="text"
                    value={editedUserInfo[field] || ''}
                    placeholder={placeholder}
                    className={styles.userInfoInput}
                    onChange={(e) => handleChange(field, e.target.value)}
                />
            </label>
        );
    };

    return createPortal(
        <div
            className={styles.userInfoModalOverlay}
            onMouseDown={onClose}
        >
            <div
                className={styles.userInfoModalBox}
                onMouseDown={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <div className={styles.userInfoModalHeader}>
                    <div className={styles.userInfoModalTitleBox}>
                        <FaUserCircle className={styles.userInfoModalIcon} />

                        <div>
                            <div className={styles.userInfoModalTitle}>
                                اطلاعات کاربر
                            </div>

                            <div className={styles.userInfoModalSubTitle}>
                                مشاهده و ویرایش اطلاعات حساب کاربری
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={styles.userInfoModalClose}
                        onClick={onClose}
                        title="بستن"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.userInfoModalBody}>
                    {!isEditing ? (
                        <div className={styles.userInfoViewBox}>
                            {renderInfoRow(<FaUser />, 'نام', userInfo.firstName)}
                            {renderInfoRow(<FaIdCard />, 'نام خانوادگی', userInfo.lastName)}
                            {renderInfoRow(<FaUserCircle />, 'نام کاربر', userInfo.username)}
                            {renderInfoRow(<FaMobileAlt />, 'شماره همراه اصلی', userInfo.mobile)}
                            {renderInfoRow(<FaPhone />, 'شماره دوم', userInfo.mobile2)}
                        </div>
                    ) : (
                        <div className={styles.userInfoEditBox}>
                            {renderInputRow(<FaUser />, 'نام', 'firstName', 'نام')}
                            {renderInputRow(<FaIdCard />, 'نام خانوادگی', 'lastName', 'نام خانوادگی')}

                            <div className={styles.userInfoReadonlyRow}>
                                <span className={styles.userInfoInputLabel}>
                                    <FaUserCircle />
                                    <span>نام کاربر</span>
                                </span>

                                <div className={styles.userInfoReadonlyValue}>
                                    {userInfo.username || '---'}
                                </div>
                            </div>

                            {renderInputRow(<FaMobileAlt />, 'شماره همراه اصلی', 'mobile', 'شماره همراه اصلی')}
                            {renderInputRow(<FaPhone />, 'شماره دوم', 'mobile2', 'شماره دوم')}
                        </div>
                    )}

                    {message && (
                        <div className={styles.userInfoMessage}>
                            {message}
                        </div>
                    )}
                </div>

                <div className={styles.userInfoModalFooter}>
                    {!isEditing ? (
                        <button
                            type="button"
                            className={styles.userInfoPrimaryBtn}
                            onClick={handleStartEdit}
                        >
                            <FaEdit />
                            ویرایش اطلاعات
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.userInfoPrimaryBtn}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                <FaSave />
                                {isSaving ? 'در حال ذخیره...' : 'ذخیره'}
                            </button>

                            <button
                                type="button"
                                className={styles.userInfoSecondaryBtn}
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                <FaTimes />
                                انصراف
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UserInfoModal;